import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogField,
  DialogFooter,
  dialogInputStyle,
  dialogTextareaStyle,
} from "@/components/ui/Dialog";
import { useUpdateBoard } from "@/hooks/useUpdateBoard";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID;
  workspaceId: UUID;
  initialName: string;
  initialDescription: string;
}

export function RenameBoardDialog({
  open,
  onOpenChange,
  boardId,
  workspaceId,
  initialName,
  initialDescription,
}: Props) {
  const nameId = useId();
  const descId = useId();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateBoard();

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmedName = name.trim();
  const trimmedDesc = description.trim();
  const nameChanged = trimmedName !== initialName;
  const descChanged = trimmedDesc !== initialDescription.trim();
  const canSubmit = trimmedName.length > 0 && (nameChanged || descChanged) && !mutation.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      // Only send description when it changed, so a pure rename never touches
      // it. When cleared, trimmedDesc is "" — update_board applies any non-None
      // value, so "" (not null) is what resets it.
      await mutation.mutateAsync({
        boardId,
        workspaceId,
        payload: { name: trimmedName, ...(descChanged ? { description: trimmedDesc } : {}) },
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.unknownError);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.editBoardTitle}
      description={t.editBoardHint}
    >
      <form onSubmit={onSubmit}>
        <DialogField label={t.boardName} htmlFor={nameId} error={error ?? undefined}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
            data-testid="rename-board-name"
          />
        </DialogField>

        <DialogField label={t.boardDescription} htmlFor={descId}>
          <textarea
            id={descId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2048}
            rows={3}
            placeholder={t.boardDescriptionPlaceholder}
            style={dialogTextareaStyle}
            data-testid="edit-board-description"
          />
        </DialogField>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            data-testid="rename-board-submit"
          >
            {mutation.isPending ? t.saving : t.save}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
